import React, { useEffect, useRef } from 'react';
import { Header } from 'components/common/header/Header';
import config from 'config/config';
import styles from './CommonEmbed.module.css';

/** 클래스 이름을 해시된 이름으로 바꿉니다. 모르는 이름은 그대로 둡니다.
 *  이름을 바꾸지 않고 스코프만 씌우려고 둔 얇은 헬퍼입니다. */
const cls = (names?: string): string =>
    (names || '').split(' ').filter(Boolean)
        .map((n) => (styles as Record<string, string>)[n] || n).join(' ');

const WIZ_COMMON_URL = config.WIZ_COMMON_URL || '';

const getCommonOrigin = (): string => {
    try { return new URL(WIZ_COMMON_URL).origin; } catch { return ''; }
};

interface CommonEmbedProps {
    title: string;
    path: string;
    /**
     * 기본 false — wiz-common 쪽 화면이 자체 헤더/뒤로가기를 갖고 있어 DOKOCREW 헤더와 겹쳐 보임.
     * 외부에서 자체 모달/시트로 감싸는 경우(설정 모달 등)도 필요 없음.
     * 명시적으로 필요할 때만 true 로.
     */
    showHeader?: boolean;
    /**
     * 일부 wiz-common 페이지(예: /terms)는 service 식별자에 따라 다른 데이터를 보여줌.
     * DOKOCREW 에서 띄울 때 서비스 ID(DOKOCREW)를 명시적으로 넘김.
     */
    service?: string;
    /**
     * iframe 내부에서 닫기 요청(CLOSE_POPUP 메시지) 시 호출.
     * 모이 측 모달을 닫는 콜백. 미지정 시 닫기 메시지는 무시됨.
     */
    onClose?: () => void;
}

export const CommonEmbed: React.FC<CommonEmbedProps> = ({ title, path, showHeader = false, service, onClose }) => {
    const iframeRef = useRef<HTMLIFrameElement>(null);
    const commonOrigin = getCommonOrigin();

    // 토큰은 postMessage로만 전달 (URL 파라미터 사용 금지)
    const params = new URLSearchParams({ popup: 'true' });
    if (service) params.set('service', service);
    const iframeSrc = `${WIZ_COMMON_URL}${path}?${params.toString()}`;

    // wiz-common 의 msgData 는 event.data 를 JSON.parse 로 처리하므로 반드시 string 으로 전송
    const sendTokenToIframe = () => {
        const token = localStorage.getItem('accessToken');
        if (token && iframeRef.current?.contentWindow) {
            iframeRef.current.contentWindow.postMessage(
                JSON.stringify({ type: 'SEND_TOKEN', data: { accessToken: token } }),
                commonOrigin || '*'
            );
        }
    };

    // iframe load 시점은 자식 React mount 보다 빠를 수 있어 listener 등록 전에 SEND_TOKEN 손실 가능.
    // 1) load 시 즉시  2) 100ms / 500ms 지연 재전송  3) 자식이 IFRAME_READY 보내면 즉시 응답
    // — 셋 중 하나만 도달하면 됨 (idempotent: 같은 토큰 재전송은 무해)
    useEffect(() => {
        const iframe = iframeRef.current;
        if (!iframe) return;
        const onLoad = () => {
            sendTokenToIframe();
            window.setTimeout(sendTokenToIframe, 100);
            window.setTimeout(sendTokenToIframe, 500);
        };
        iframe.addEventListener('load', onLoad);
        return () => iframe.removeEventListener('load', onLoad);
    }, [path]);

    // iframe → 부모 메시지 수신 (IFRAME_READY 응답 / CLOSE_POPUP 모달 닫기)
    useEffect(() => {
        const handler = (e: MessageEvent) => {
            if (commonOrigin && e.origin !== commonOrigin) return;
            const raw = e.data;
            let payload: any = raw;
            if (typeof raw === 'string') {
                try { payload = JSON.parse(raw); } catch { return; }
            }
            const type = (payload?.type as string)?.toUpperCase();
            if (type === 'IFRAME_READY') {
                sendTokenToIframe();
            } else if (type === 'CLOSE_POPUP') {
                onClose?.();
            }
        };
        window.addEventListener('message', handler);
        return () => window.removeEventListener('message', handler);
    }, [commonOrigin, onClose]);

    return (
        <div className={cls("common-embed-wrapper")}>
            {showHeader && <Header title={title} isSubPage={true} />}
            <div className={cls("common-embed-content")}>
                <iframe
                    ref={iframeRef}
                    src={iframeSrc}
                    title={title}
                    className={cls("common-iframe")}
                    sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-modals"
                />
            </div>
        </div>
    );
};
